import React, { useState } from 'react';
import { useRoomStore } from '@/stores/roomStore';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useToastStore } from '@/components/Toast';

interface ShareLinkProps {
  roomId: string;
}

export const ShareLink: React.FC<ShareLinkProps> = ({ roomId }) => {
  const { room } = useRoomStore();
  const addToast = useToastStore((state) => state.addToast);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/join/${roomId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      addToast('Room link copied to clipboard', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      addToast('Failed to copy link', 'error');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${room?.name || 'our room'} on YT Music Sync`,
          text: 'Listen to music together in sync!',
          url: shareUrl
        });
        addToast('Shared successfully', 'success');
      } catch (error) {
        // User cancelled share
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="bg-lime-400/10 rounded-xl shadow-sm p-6 border border-lime-400/30">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-lime-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
        </svg>
        <h2 className="text-lg font-bold text-primary">Invite Friends</h2>
      </div>

      <p className="text-sm text-secondary mb-4">
        Share this link with friends to invite them to {room?.name || 'the room'}
      </p>

      <div className="space-y-3">
        <div className="flex gap-2">
          <Input
            value={shareUrl}
            readOnly
            className="font-mono text-sm"
          />
          <Button
            variant="primary"
            onClick={handleCopy}
            className="flex-shrink-0"
          >
            {copied ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
              </svg>
            )}
          </Button>
        </div>

        {typeof navigator.share !== 'undefined' && (
          <Button
            variant="secondary"
            onClick={handleShare}
            className="w-full"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
            </svg>
            Share via...
          </Button>
        )}

        <div className="flex items-center gap-3 pt-3 border-t border-lime-400/20">
          <div className="flex-shrink-0 w-16 h-16 bg-muted rounded-lg flex items-center justify-center border-2 border-lime-400 shadow-sm">
            <div className="text-xl font-bold text-lime-400">{roomId}</div>
          </div>
          <div className="flex-1 text-sm">
            <p className="font-bold text-primary">Room Code</p>
            <p className="text-tertiary">Friends can also join using this code</p>
          </div>
        </div>
      </div>
    </div>
  );
};
